// We should use the primitives
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@radix-ui/react-popover";
import { Command as CommandPrimitive } from "cmdk";
import { Check, ChevronDown, ChevronRight, X } from "lucide-react";
import {
  type KeyboardEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

import { Badge } from "./badge";
import { Button } from "./button";
import {
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "./command";
import { Shortcut } from "./shortcut";
import { Skeleton } from "./skeleton";

export type Option = {
  value: string | number | null | undefined;
  label: string;
  subLabel?: string;
  subChildren?: Option[];
};

export type AutoCompleteProps = {
  options: Option[];
  EmptyListComponent?: ({
    inputValue,
  }: {
    inputValue: string;
  }) => React.ReactElement;
  value?: Option["value"];
  onValueChange?: (value: Option | null) => void;
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  selectTextOnFocus?: boolean;
  fullWidth?: boolean;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  showEmptyLabel?: boolean;
  filterBy?: (
    inputValue: string,
    value: Option["value"],
    label: Option["label"],
    subLabel: Option["subLabel"],
  ) => boolean;
  side?: "top" | "right" | "bottom" | "left";
  sideOffset?: number;
  autoFocus?: boolean;
  filterOnOpen?: boolean;
  nullable?: boolean;
};

export const AutoComplete = forwardRef<HTMLInputElement, AutoCompleteProps>(
  (
    {
      options: _options,
      placeholder,
      EmptyListComponent,
      value,
      onValueChange,
      className,
      disabled,
      side = "bottom",
      sideOffset = 4,
      onBlur,
      onFocus,
      isLoading = false,
      selectTextOnFocus = true,
      fullWidth = false,
      filterBy = _filterBy,
      onKeyDown,
      autoFocus,
      showEmptyLabel = false,
      filterOnOpen = true,
      nullable = true,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement>(null);

    const getLabel = (placeholder?: string) => {
      const feminineNouns = ["Kostenstelle"];
      const isFeminine = feminineNouns.includes(placeholder ?? "");
      return isFeminine
        ? `${t("none_feminine")} ${placeholder ?? ""}`
        : `${t("none")} ${placeholder ?? ""}`;
    };

    const opts = useMemo(() => {
      return [
        ...(nullable ? [{ value: null, label: getLabel(placeholder) }] : []),
        ..._options,
      ];
    }, [_options, placeholder, nullable, t]);

    const options = useMemo(() => {
      return opts.reduce((acc, option) => {
        acc.push(option);
        if (option.subChildren) {
          acc.push(...option.subChildren);
        }
        return acc;
      }, [] as Option[]);
    }, [opts]);

    const [isOpen, setOpen] = useState(false);
    const [selected, setSelected] = useState<Option["value"]>(value);
    const [inputValue, setInputValue] = useState<string>(
      options.find((item) => item.value === value)?.label?.toString() ?? "",
    );
    const [isDirty, setIsDirty] = useState(false);

    useEffect(() => {
      setSelected(value);

      const inputValue = getInputValue(
        value,
        options.find((item) => item.value === value)?.label?.toString() ?? "",
        showEmptyLabel,
      );

      setInputValue(inputValue);
    }, [options, value, showEmptyLabel]);

    const _selectedOption = options.find((option) => option.value === selected);

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        const input = inputRef.current;

        if (!input) {
          return;
        }

        //@ts-ignore hackity hack to ignore keyboard enter and only use programmatic enter
        if (event.key === "Enter" && event.nativeEvent.isTrusted) {
          // if (input.value === "") onKeyDown?.(event); // TODO evaluate if this is necessary
          return;
        }

        if (!isOpen) {
          setOpen(true);
        }

        if (event.key === "Escape") {
          input.blur();
        }

        onKeyDown?.(event);
      },
      [isOpen, onKeyDown],
    );

    const handleBlur = useCallback(
      (event: React.FocusEvent<HTMLInputElement>) => {
        setOpen(false);

        const inputValue = getInputValue(
          _selectedOption?.value,
          _selectedOption?.label,
          showEmptyLabel,
        );

        setInputValue(inputValue);
        setIsDirty(false);
        onBlur?.(event);
      },
      [_selectedOption, onBlur],
    );

    const handleFocus = useCallback(
      (event: React.FocusEvent<HTMLInputElement>) => {
        selectTextOnFocus && inputRef.current?.select();
        setOpen(true);
        onFocus?.(event);
      },
      [onFocus, selectTextOnFocus],
    );

    const handleSelectOption = useCallback(
      (selectedOption: Option, blurInput = true) => {
        const inputValue = getInputValue(
          selectedOption.value,
          selectedOption.label,
          showEmptyLabel,
        );
        setInputValue(inputValue);
        setSelected(selectedOption.value);
        onValueChange?.(selectedOption);

        // This is a hack to prevent the input from being focused after the user selects an option
        // We can call this hack: "The next tick"
        setTimeout(() => {
          if (onKeyDown && blurInput) {
            const event = new KeyboardEvent("keydown", {
              key: "Enter", // The key value for Enter
              code: "Enter", // The physical key code for Enter
              keyCode: 13, // The keyCode for Enter (deprecated but widely used)
              charCode: 13, // Deprecated, included for compatibility
              shiftKey: false, // Shift key not pressed
              ctrlKey: false, // Control key not pressed
              metaKey: false, // Meta key (Command/Windows) not pressed
              altKey: false, // Alt key not pressed
              bubbles: true, // Event bubbles up through the DOM
              cancelable: true, // Event can be canceled
            });
            inputRef.current?.dispatchEvent(event);
          }

          blurInput && inputRef?.current?.blur();
        }, 0);
      },
      [onValueChange, onKeyDown],
    );

    // TODO - revisit this in the future. Should we use the internal prop of cmdk or have the custom function.
    const _filteredOptions = useMemo(() => {
      const emptyOption = opts.find((option) => option.value === null);

      const _inputValue = emptyOption?.label !== inputValue ? inputValue : "";

      return opts.reduce((acc, option) => {
        if (option.subChildren?.length) {
          const subChildren = option.subChildren.filter((subOption) => {
            return (
              (!filterOnOpen && !isDirty) ||
              filterBy(
                _inputValue,
                subOption.value,
                subOption.value !== null ? subOption.label : "",
                subOption.subLabel,
              )
            );
          });

          if (subChildren.length) {
            acc.push({
              ...option,
              subChildren,
            });
          }

          return acc;
        }

        if (
          (!filterOnOpen && !isDirty) ||
          filterBy(
            _inputValue,
            option.value,
            option.value !== null ? option.label : "",
            option.subLabel,
          )
        ) {
          acc.push(option);
        }

        return acc;
      }, [] as Option[]);
    }, [inputValue, opts, filterBy]);

    const handleInputChange = useCallback((search: string) => {
      setIsDirty(true);
      setInputValue(search);
    }, []);

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    useEffect(() => {
      if (selected && isOpen) {
        setTimeout(() => {
          const element = document.querySelector(`[data-value="${selected}"]`);
          if (element) {
            element.scrollIntoView({
              block: "center",
            });
          }
        }, 0);
      }
    }, [selected, isOpen]);

    return (
      <CommandPrimitive shouldFilter={false} className="w-full">
        <Popover open={isOpen}>
          <PopoverAnchor>
            <div
              className={cn(
                "flex flex-row gap-1 border border-input bg-background rounded-md items-center ring-offset-background",
                isOpen
                  ? "ring-2 ring-muted-foreground ring-offset-0"
                  : "ring-transparent",
                className,
              )}
            >
              <div className="flex-1">
                <CommandPrimitive.Input
                  ref={inputRef}
                  value={inputValue}
                  onValueChange={isLoading ? undefined : handleInputChange}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                  onKeyDown={handleKeyDown}
                  placeholder={placeholder ?? t("search")}
                  disabled={disabled}
                  autoFocus={autoFocus}
                  className={cn(
                    "flex h-[38px] w-full pl-3 text-sm bg-transparent py-3 outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 overflow-ellipsis",
                  )}
                />
              </div>
              <Button
                disabled={disabled}
                variant="ghost"
                size="sm"
                type="reset"
                tabIndex={-1}
                className="h-auto p-1 mr-1"
                onClick={() => {
                  handleSelectOption({ value: null, label: "" }, false);
                  inputRef.current?.focus();
                }}
              >
                <X
                  size="16"
                  className={cn(
                    !_selectedOption?.value && "text-muted-foreground",
                  )}
                />
              </Button>
            </div>
          </PopoverAnchor>
          <PopoverContent
            className={cn(
              "z-[50] rounded-md shadow-md bg-white data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
              fullWidth ? "w-full" : "w-96",
            )}
            align="start"
            side={side}
            sideOffset={sideOffset}
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <CommandList className="rounded-md border">
              {isLoading ? (
                <CommandPrimitive.Loading>
                  <div className="p-1">
                    <Skeleton className="h-8 w-full" />
                  </div>
                </CommandPrimitive.Loading>
              ) : null}
              {options.length > 0 && !isLoading ? (
                <CommandGroup>
                  {_filteredOptions.map((option) => {
                    return (
                      <_CommandItem
                        key={option.value}
                        option={option}
                        selected={selected}
                        onSelect={handleSelectOption}
                      />
                    );
                  })}
                </CommandGroup>
              ) : null}
              {!isLoading ? (
                <CommandEmpty
                  className="select-none rounded-sm px-2 pt-1 pb-3 text-center text-sm"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setTimeout(() => {
                      inputRef?.current?.blur();
                    }, 200);
                  }}
                >
                  {EmptyListComponent ? (
                    <EmptyListComponent inputValue={inputValue} />
                  ) : (
                    t("no_results")
                  )}
                </CommandEmpty>
              ) : null}
            </CommandList>
          </PopoverContent>
        </Popover>
      </CommandPrimitive>
    );
  },
);

type _CommandItemsProps = {
  option: Option;
  selected: Option["value"];
  onSelect: (option: Option) => void;
};

const _CommandItem = ({ option, selected, onSelect }: _CommandItemsProps) => {
  const isSelected = selected === option.value;
  const [expanded, setExpanded] = useState(true);

  return (
    <>
      <_Item
        key={option.value}
        option={option}
        onSelect={() => onSelect(option)}
        isSelected={isSelected}
        expanded={expanded}
        onExpand={() => setExpanded(!expanded)}
      />
      {expanded &&
        option.subChildren?.map((subOption) => {
          const isSelected = selected === subOption.value;

          return (
            <_Item
              isSubChild
              key={subOption.value}
              option={subOption}
              onSelect={() => onSelect(subOption)}
              isSelected={isSelected}
            />
          );
        })}
    </>
  );
};

type _ItemProps = {
  option: Option;
  onSelect: () => void;
  isSelected: boolean;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  isSubChild?: boolean;
  expanded?: boolean;
  onExpand?: () => void;
};

const _Item = ({
  option: { value, label, subLabel, subChildren },
  onSelect,
  isSelected,
  className,
  isSubChild = false,
  expanded,
  onExpand,
}: _ItemProps) => {
  const { t } = useTranslation();

  return (
    <CommandItem
      key={value}
      value={value?.toString()}
      onMouseDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onSelect={onSelect}
      className={cn(
        "flex w-full items-center gap-2 group",
        !isSelected && !isSubChild ? "pl-8" : null,
        !isSelected && isSubChild ? "pl-16" : null,
        isSubChild ? "pl-16" : null,
        className,
      )}
    >
      {isSelected ? <Check className="w-4" /> : null}
      {Boolean(subChildren?.length) && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onExpand?.();
          }}
          style={{ cursor: "pointer" }}
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
      )}
      {subLabel && (
        <Badge variant="secondary" className="px-1">
          {subLabel}
        </Badge>
      )}
      {label}
      <div className="ml-auto pl-1 opacity-0 group-aria-selected:opacity-100 transition-opacity">
        <div className="flex items-center gap-1 bg-border rounded px-1.5 py-1">
          <span className="text-[10px] capitalize leading-none">
            {t("enter")}
          </span>
          <Shortcut.ShortcutEnter size="sm" className="bg-transparent p-0" />
        </div>
      </div>
    </CommandItem>
  );
};

const _filterBy = (
  inputValue: string,
  value: Option["value"],
  label: Option["label"],
  subLabel: Option["subLabel"],
) => {
  return label.toLowerCase().includes(inputValue.toLowerCase());
};

const getInputValue = (
  value: Option["value"],
  label: Option["label"] | undefined,
  showEmptyLabel: boolean,
) => {
  let inputValue = "";

  if (value || (value === null && showEmptyLabel)) {
    inputValue = label ?? "";
  }

  return inputValue;
};
